import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader } from '@truecare/ui';
import type {
  AdminArAgingFacilityRow,
  AdminArAgingResponse,
} from '@truecare/schemas';
import { unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { adminBeforeLoad } from '../../lib/admin-before-load';
import { AdminLayout } from '../../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../../components/AdminTable';

/**
 * Phase 14d-3 — AR aging page.
 *
 * 4-bucket facility AR breakdown (current / 1-30 / 31-60 / 60+ days past
 * due). Operator's morning AR-triage view — sorted worst-aged first so
 * the most-urgent facilities surface at the top. Each cell drills into
 * the existing /admin/invoices page filtered to the relevant facility +
 * status combination.
 *
 * Header KPI strip shows aggregate totals (mirrors the dashboard
 * `billing.ar_aging_summary` card); facility-level table sits below.
 *
 * Homecare invoices excluded — they prepay via Stripe (Phase 10d) and
 * have no overdue rail.
 */

export const Route = createFileRoute('/billing/ar-aging')({
  beforeLoad: () => adminBeforeLoad('/billing/ar-aging'),
  component: ArAgingPage,
});

function ArAgingPage() {
  const query = useQuery<AdminArAgingResponse>({
    queryKey: ['admin', 'billing', 'ar-aging'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/billing/ar-aging' as never),
      )) as unknown as AdminArAgingResponse,
  });

  return (
    <AdminLayout>
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-forest md:text-4xl">AR aging</h1>
        <p className="font-body text-sm text-ink-muted">
          Outstanding facility AR by aging bucket. Sorted worst-aged first.
          Click a cell to drill into the filtered invoice list. Homecare AR
          is prepaid via Stripe so it isn't included here.
        </p>
      </header>

      <TotalsStrip totals={query.data?.totals} />
      <FacilityBreakdown
        rows={query.data?.facilities ?? []}
        isLoading={query.isPending}
        isError={query.isError}
      />
    </AdminLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Header KPI strip — 4 buckets across the top
// ─────────────────────────────────────────────────────────────────────

interface Totals {
  current: number;
  oneToThirty: number;
  thirtyOneToSixty: number;
  overSixty: number;
  totalOutstandingCents: number;
  invoiceCount: number;
}

function TotalsStrip({ totals }: { totals: Totals | undefined }) {
  const cards: {
    label: string;
    cents: number;
    tone: 'ok' | 'warning' | 'critical';
    testId: string;
  }[] = totals
    ? [
        { label: 'Current', cents: totals.current, tone: 'ok', testId: 'ar-totals-current' },
        { label: '1-30 days', cents: totals.oneToThirty, tone: 'ok', testId: 'ar-totals-1-30' },
        { label: '31-60 days', cents: totals.thirtyOneToSixty, tone: 'warning', testId: 'ar-totals-31-60' },
        { label: '60+ days', cents: totals.overSixty, tone: 'critical', testId: 'ar-totals-60-plus' },
      ]
    : [];

  return (
    <div
      className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="ar-aging-totals-strip"
    >
      {cards.map((c) => (
        <BucketCard key={c.label} {...c} />
      ))}
    </div>
  );
}

function BucketCard({
  label,
  cents,
  tone,
  testId,
}: {
  label: string;
  cents: number;
  tone: 'ok' | 'warning' | 'critical';
  testId: string;
}) {
  const tones: Record<typeof tone, string> = {
    ok: 'border-border',
    warning: 'border-warning',
    critical: 'border-danger',
  };
  // An empty bucket carries no alert colour regardless of its tier — otherwise a
  // $0.00 "60+ days" card glows red while the one funded bucket looks milder,
  // drawing the eye to the wrong place.
  const shownTone = cents > 0 ? tone : 'ok';
  // Audit M7 (2026-09-01) — the tone was conveyed by border colour ALONE (WCAG
  // 1.4.1); a red-green-deficient or fast-scanning operator got no cue on which
  // AR bucket is dangerously overdue. Add a redundant text label (mirrors the
  // dashboard MetricCard's "⚠ Critical" pattern), so severity reads at greyscale.
  const toneLabel: Record<typeof tone, string | null> = {
    ok: null,
    warning: '⚠ Ageing — chase',
    critical: '⚠ Overdue — action needed',
  };
  const label2 = toneLabel[shownTone];
  return (
    <div
      className={`rounded-md border ${tones[shownTone]} bg-white p-4`}
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-2 font-heading text-2xl text-forest">
        {formatDollars(cents)}
      </div>
      {label2 && (
        <div
          className={`mt-1 text-xs font-medium ${shownTone === 'critical' ? 'text-danger' : 'text-warning'}`}
          data-testid={`${testId}-tone`}
        >
          {label2}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Facility breakdown table — one row per facility with open AR
// ─────────────────────────────────────────────────────────────────────

function FacilityBreakdown({
  rows,
  isLoading,
  isError,
}: {
  rows: AdminArAgingFacilityRow[];
  isLoading: boolean;
  isError: boolean;
}) {
  const navigate = useNavigate();

  // AdminTable keys rows on `id`; the AR-aging row's natural id is its
  // facilityId, so alias it in for the table's row-key + click handler.
  type FacilityRow = AdminArAgingFacilityRow & { id: string };
  const tableRows: FacilityRow[] = rows.map((r) => ({ ...r, id: r.facilityId }));

  const COLUMNS: AdminTableColumn<FacilityRow>[] = [
    {
      key: 'facilityName',
      header: 'Facility',
      sortable: false,
      render: (r) => r.facilityName,
    },
    {
      key: 'current',
      header: 'Current',
      width: 'w-32',
      sortable: false,
      render: (r) => <BucketCell row={r} cents={r.current} navigate={navigate} bucketHint="current" />,
    },
    {
      key: 'oneToThirty',
      header: '1-30',
      width: 'w-32',
      sortable: false,
      render: (r) => <BucketCell row={r} cents={r.oneToThirty} navigate={navigate} bucketHint="1-30" />,
    },
    {
      key: 'thirtyOneToSixty',
      header: '31-60',
      width: 'w-32',
      sortable: false,
      render: (r) => <BucketCell row={r} cents={r.thirtyOneToSixty} navigate={navigate} bucketHint="31-60" />,
    },
    {
      key: 'overSixty',
      header: '60+',
      width: 'w-32',
      sortable: false,
      render: (r) => <BucketCell row={r} cents={r.overSixty} navigate={navigate} bucketHint="60+" />,
    },
    {
      key: 'totalOutstandingCents',
      header: 'Total',
      width: 'w-32',
      sortable: false,
      render: (r) => (
        <span className="font-medium" data-testid={`ar-row-${r.facilityId}-total`}>
          {formatDollars(r.totalOutstandingCents)}
        </span>
      ),
    },
    {
      key: 'invoiceCount',
      header: '# invoices',
      width: 'w-24',
      sortable: false,
      render: (r) => r.invoiceCount,
    },
  ];

  return (
    <Card padding="md" className="mt-6" data-testid="ar-aging-facility-card">
      <CardHeader
        title={`Per-facility breakdown${rows.length > 0 ? ` (${rows.length})` : ''}`}
        description="Click a facility name to open the facility detail page (invoice actions live there). Click any bucket cell to drill into the filtered invoice list."
      />
      <AdminTable<FacilityRow>
        columns={COLUMNS}
        rows={tableRows}
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No facilities have open AR. Nice."
        page={1}
        limit={100}
        totalCount={rows.length}
        onPageChange={() => undefined}
        onRowClick={(r) => navigate({ to: '/facilities/$id', params: { id: r.facilityId } })}
      />
    </Card>
  );
}

function BucketCell({
  row,
  cents,
  navigate,
  bucketHint,
}: {
  row: AdminArAgingFacilityRow;
  cents: number;
  navigate: ReturnType<typeof useNavigate>;
  bucketHint: string;
}) {
  if (cents === 0) {
    return <span className="text-ink-muted" data-testid={`ar-cell-${row.facilityId}-${bucketHint}-empty`}>—</span>;
  }
  return (
    <button
      type="button"
      data-testid={`ar-cell-${row.facilityId}-${bucketHint}`}
      className="text-left underline-offset-2 hover:text-forest hover:underline"
      onClick={(e) => {
        e.stopPropagation();
        // Drill into the invoice list filtered to this facility's open
        // statuses. The aging-bucket is implicit (operator picks the
        // bucket by reading the cell label); /admin/invoices doesn't
        // have a "due in past X-Y days" filter today, but facilityId +
        // status=ISSUED/PARTIAL/OVERDUE narrows the result enough for
        // v1. v1.x could add an aging-window query param if useful.
        // For now we route to the facility detail page (where the
        // invoice section lands in Phase 14e) — the cleanest drill
        // until /admin/invoices itself has a UI.
        navigate({ to: '/facilities/$id', params: { id: row.facilityId } });
      }}
    >
      {formatDollars(cents)}
    </button>
  );
}

function formatDollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
